import type { Spx } from '@wix/thunderbolt-becky-types'

const HALF_PX = '0.5px'

export const CONTAINER_TYPES = {
	Page: true,
	MasterPage: true,
	AppPage: true,
	VerticalRepeater: true,
	QuickActionBar: true,
	PageGroup: true,
	SiteRegionContainer: true,
	Container: true,
	MediaBox: true,
	HoverBox: true,
	PopupContainer: true,
	Group: true,
	FormContainer: true,
	WSiteStructure: true,
	HeaderContainer: true,
	FooterContainer: true,
	PagesContainer: true,
	StripContainer: true,
	StripColumnsContainer: true,
	ClassicSection: true,
	Column: true,
	MediaPlayer: true,
	ScreenWidthContainer: true,
	Area: true,
	BoxSlideShow: true,
	StateBox: true,
	StateStrip: true,
	StateBoxState: true,
	StateBoxFormState: true,
	StateStripState: true,
	StripContainerSlideShow: true,
	SlideShowSlide: true,
	BoxSlideShowSlide: true,
	StripContainerSlideShowSlide: true,
	Repeater: true,
	MediaContainer: true,
	MenuContainer: true,
	AppWidget: true,
	Popover: true,
	RefComponent: true,
	Section: true,
	HeaderSection: true,
	FooterSection: true,
	MembersAreaSection: true,
	MultiStateBox: true,
	MegaMenuContainerItem: true,
	StretchedContainer: true,
	SettingsPanelContainer: true,
	SingleTab: true,
	AccordionItem: true,
	SelectableContainer: true,
	HamburgerMenuContainer: true,
}

export const meshIgnoredComponentTypes: Record<string, boolean> = {
	BackgroundGroup: true,
	Repeater: true,
	PagesContainer: true,
	PageGroup: true,
	Column: true, // mesh for column will be calculated by StripColumnsContainer
	StripContainerSlideShow: true,
	BoxSlideShow: true,
	PinnedLayer: true,
	MeshGroup: true,
	MegaMenuContainerItem: true,
}

export const SPX_VALUE_REGEX = /-?[0-9]*\.?[0-9]*spx/

const roundToFactor = (value: number, numberOfDecimals: number) => {
	let factor = Math.pow(10, numberOfDecimals)
	let roundedNumber = Math.round(value * factor) / factor
	while (roundedNumber === 0 && value !== 0) {
		factor *= 10
		roundedNumber = Math.round(value * factor) / factor
	}
	return roundedNumber
}

export const getSpxScalingCssValue = (scalingValue: number) => {
	return `calc((${scalingValue} * var(--one-unit)) - (var(--scrollbar-width) * ${roundToFactor(
		scalingValue / 100,
		7
	)}))`
}

export const getCqwUnit = (numericValue: number, refWidth: number) => roundToFactor((numericValue / refWidth) * 100, 7)

const fixCssUnits = (css: string) => {
	let bracketsCounter = 0

	for (let i = 0; i < css.length; i++) {
		if (css[i] === '(') {
			bracketsCounter++
		} else if (css[i] === ')') {
			bracketsCounter--
		} else if (bracketsCounter === 0 && css[i].match(/\d/) && (css[i + 1] === ' ' || i === css.length - 1)) {
			css = css.slice(0, i + 1) + 'px' + css.slice(i + 1)
		}
	}
	return css
}

const getCalcCssValue = (numericValue: number, refWidth: number, shouldRoundCssValue: boolean = false) => {
	const scalingFactor = `${Number(
		(numericValue / refWidth).toFixed(7) // numericValue / --refWidth * 100
	)} * (var(--scaling-factor) - var(--scrollbar-width))`

	const clampedValue = numericValue < 0 ? `min(-${HALF_PX}, ${scalingFactor})` : `max(${HALF_PX}, ${scalingFactor})`

	if (shouldRoundCssValue) {
		return `round(${clampedValue}, 0.5px)`
	}

	return clampedValue
}

export const resolveSpxAttribute = (
	value: string | number,
	spx?: Spx | undefined,
	keepNumValues = false,
	shouldRoundCssValue = false
) => {
	const valuesWithResolvedSpx = `${value}`.replace(new RegExp(SPX_VALUE_REGEX, 'g'), (spxValue) =>
		resolveSpxValue(spxValue, spx, shouldRoundCssValue)
	)

	return keepNumValues ? valuesWithResolvedSpx : fixCssUnits(valuesWithResolvedSpx)
}

export const resolveSpxValue = (spxValue: string, spx: Spx | undefined, shouldRoundCssValue: boolean = false) => {
	const { refWidth, resolverType } = spx ?? {}
	const isScale = resolverType === 'scale'
	const numericValue = parseFloat(spxValue)
	return isNaN(numericValue)
		? spxValue
		: isScale && refWidth && numericValue !== 0
			? getCalcCssValue(numericValue, refWidth, shouldRoundCssValue)
			: `${numericValue}px`
}

export const getSpxValueResolver = (spx: Spx | undefined, shouldRoundCssValue?: boolean) => (value: string) =>
	resolveSpxValue(value, spx, shouldRoundCssValue)
